import * as React from "react";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  TableContainer,
} from "@mui/material";
import { useLocation } from "react-router-dom";

export interface OrderRecord {
  _id: string;
  orderNumber: string;
  quantityAirbag: number;
  quantityBin: number;
  status: string;
  provisionalDate: string;
}

interface OrdersListProps {
  orders: OrderRecord[];
}

const statusChoices = [
  { id: "IN_PROGRESS", name: "En cours" },
  { id: "COMPLETED", name: "Terminé" },
  { id: "CANCELLED", name: "Annulé" },
];

const statusLabels = statusChoices.reduce(
  (acc, { id, name }) => ({ ...acc, [id]: name }),
  {} as Record<string, string>
);

const statusColorMap: Record<string, "success" | "warning" | "error"> = {
  IN_PROGRESS: "warning",
  COMPLETED: "success",
  CANCELLED: "error",
};

const API_URL =
  process.env.REACT_APP_API_URL || "https://aihubcy.hubency.com/api";

export const OrdersList: React.FC<OrdersListProps> = ({ orders }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // QR dialog state
  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrImageUrl, setQrImageUrl] = React.useState<string | null>(null);
  const [qrLoading, setQrLoading] = React.useState(false);
  const [qrError, setQrError] = React.useState<string | null>(null);

  const inviteToken = new URLSearchParams(useLocation().search).get(
    "inviteToken"
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedOrders = React.useMemo(
    () => orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [orders, page, rowsPerPage]
  );

  const fetchQr = async (orderId: string) => {
    if (!inviteToken) {
      setQrError("Invite token manquant");
      return setQrOpen(true);
    }
    setQrLoading(true);
    setQrError(null);
    try {
      const res = await fetch(
        `${API_URL}/orders/${orderId}/qr?inviteToken=${inviteToken}`
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setQrImageUrl(url);
    } catch (err: any) {
      setQrError(err.message);
    } finally {
      setQrLoading(false);
      setQrOpen(true);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Vos commandes
        </Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Numéro BDC</TableCell>
                <TableCell align="center">Quantité de caisse</TableCell>
                <TableCell align="center">Quantité d'airbag</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="center">Date prévisionnelle</TableCell>
                <TableCell align="center">QR Code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.map((o) => (
                <TableRow key={o._id}>
                  <TableCell align="center">{o.orderNumber}</TableCell>
                  <TableCell align="center">{o.quantityBin}</TableCell>
                  <TableCell align="center">{o.quantityAirbag}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={statusLabels[o.status] ?? o.status}
                      color={statusColorMap[o.status] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {new Date(o.provisionalDate).toLocaleDateString("fr-FR")}
                  </TableCell>
                  <TableCell align="center">
                    {o.status === "IN_PROGRESS" ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => fetchQr(o._id)}
                      >
                        Voir QR
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={orders.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      </Paper>

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>QR Code du BDC</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          {qrLoading && <CircularProgress />}
          {qrError && <Alert severity="error">{qrError}</Alert>}
          {qrImageUrl && (
            <img
              src={qrImageUrl}
              alt="QR Code"
              style={{ maxWidth: "100%", height: "auto", marginTop: 16 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
